/**
 * Полоса фильтра потока разговоров: период со стрелками, категория разговора,
 * проекты. Одна на «Возражения» (#846) и «Голос клиента» (#848).
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PeriodBar, type Period } from '@/components/layout/PeriodBar';
import { ProjectMultiSelect } from '@/components/filters';
import type { usePeriodNavigation } from '@/hooks/usePeriodNavigation';
import { OBJECTIONS_PERIODS } from '@/components/analytics/objections/objections-search';
import { CallCategoryFilter } from './CallCategoryFilter';
import { ALL_CATEGORIES, type StreamSearch } from './useStreamWindow';

/** Both filters one width: a long project name or category is cut, not the row. */
const FILTER_WIDTH = 'w-44';

export interface StreamFilterBarProps {
  period: Period;
  navigation: ReturnType<typeof usePeriodNavigation>['navigation'];
  projectIds: string[];
  /** Not needed when `hideCategory`. */
  callCategory?: string;
  /**
   * Leave out the call-category select: for a report whose subject is not a
   * stream of calls by category (the script steps report, #879 — a script is
   * already bound to its category).
   */
  hideCategory?: boolean;
  isLoading: boolean;
  onSearchChange: (patch: Partial<StreamSearch>) => void;
  /** Controls after the filters — the tab's export menu. */
  extraActions?: ReactNode;
}

export function StreamFilterBar({
  period,
  navigation,
  projectIds,
  callCategory = ALL_CATEGORIES,
  hideCategory = false,
  isLoading,
  onSearchChange,
  extraActions,
}: StreamFilterBarProps) {
  const { t } = useTranslation('dashboard');
  return (
    <PeriodBar
      periods={[
        { id: 'today', label: t('supervisorDashboard.period.today') },
        { id: 'week', label: t('supervisorDashboard.period.week') },
        { id: 'month', label: t('supervisorDashboard.period.month') },
        { id: 'quarter', label: t('supervisorDashboard.period.quarter') },
      ]}
      activePeriod={period}
      onPeriodChange={(next) =>
        // A new period starts from today, in the same update — no scan of
        // the new period around the old anchor.
        onSearchChange({
          period: OBJECTIONS_PERIODS.find((p) => p === next),
          anchor: undefined,
          from: undefined,
          to: undefined,
          day: undefined,
          demo: undefined,
        })
      }
      navigation={navigation}
      isLoading={isLoading}
      actions={
        // The filters are one group that never splits: category and projects
        // side by side, the same width; on a narrow panel the group and the
        // export wrap as wholes (#2315).
        <div className="flex flex-wrap items-center justify-end gap-2">
          <div data-testid="stream-filters" className="flex items-center gap-2">
            {!hideCategory && (
              <CallCategoryFilter
                className={FILTER_WIDTH}
                value={callCategory}
                onChange={(category) => onSearchChange({ category })}
              />
            )}
            <ProjectMultiSelect
              className={FILTER_WIDTH}
              selectedProjectIds={projectIds}
              onChange={(ids) =>
                onSearchChange({ projectIds: ids.length > 0 ? ids : undefined })
              }
            />
          </div>
          {extraActions}
        </div>
      }
    />
  );
}
